import { ReactElement, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, Typography, Theme } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import DataTable from '@src/components/DataTable'
import { DataColumn } from '@src/components/DataTable/DataTable'
import {
  SemiConfigClone,
  useProductFromQueryParam,
  getProductBasedUniqueIdName,
  convertToUppercaseBasedOnCondition,
} from '../../ProductsConfiguration'
import TableExpanded from './TableExpanded'
import SelectAllCheckbox from './SelectAllCheckbox'
import SelectSingleCheckbox from './SelectSingleCheckbox'
import TableRowDetailWithExpansion from './TableRowDetailWithExpansion'
import HoverToolTip from '@src/components/HoverToolTip'
import { SemiConfigProductTypes } from '../../enum'
import DoneRoundedIcon from '@material-ui/icons/DoneRounded'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(8),
  },
  instructions: {
    padding: theme.spacing(8, 0),
    '& h6': {
      fontWeight: 700,
    },
  },
  table: {
    display: 'flex',
    '& th': {
      verticalAlign: 'middle',
    },
    '& .MuiTableCell-root': {
      padding: theme.spacing(2, 1),
    },
  },
  tooltipClass: {
    maxWidth: 'none',
    width: 'auto',
  },
  topChoice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  topChoiceIcon: {
    color: theme.palette.primary.main,
  },
}))
export interface DataCount {
  pageStart: number
  pageEnd: number
  totalCount: number
}
interface ClonesTableProps {
  tableTitle: ReactElement
  tableData: SemiConfigClone[]
  dataCount: DataCount
}

const ClonesTable = ({
  tableTitle,
  tableData,
  dataCount,
}: ClonesTableProps) => {
  const classes = useStyles()
  const product = useProductFromQueryParam()
  const uniqueIdName = getProductBasedUniqueIdName(product)
  const [expandedList, setExpandedList] = useState<string[]>([])
  const {
    SANGERCRISPR,
    ORF,
    PREDESIGNED_SIRNA,
    PREDESIGNED_CRISPR_GRNA,
    PREDESIGNED_SHRNA,
  } = SemiConfigProductTypes

  const toggleDetail = (id: string) => {
    const updatedList = expandedList.includes(id)
      ? expandedList.filter((expanded) => expanded !== id)
      : [...expandedList, id]
    setExpandedList(updatedList)
  }
  const checkForExpanded = (id?: string) => {
    return !!(id && expandedList.includes(id))
  }

  const getProductBasedTableColumns = (): DataColumn<SemiConfigClone>[] => {
    const chekBoxColumn: DataColumn = {
      headElement: <SelectAllCheckbox searchResults={tableData} />,
      bodyElement: (data: SemiConfigClone) => (
        <SelectSingleCheckbox clone={data} />
      ),
      width: '5%',
      textAlign: 'center',
    }
    const geneIdColumn: DataColumn = {
      headElement: (
        <FormattedMessage id="CONF_CRISPR_GENE_ID" defaultMessage="Gene ID" />
      ),
      bodyElement: (data: SemiConfigClone) =>
        convertToUppercaseBasedOnCondition(data?.geneId),
      width: '10%',
      textAlign: 'center',
    }
    const speciesColumn: DataColumn = {
      headElement: (
        <FormattedMessage
          id="CONF_SPECIES_crisprgrna"
          defaultMessage="Species"
        />
      ),
      bodyElement: (data) => convertToUppercaseBasedOnCondition(data?.species),
      width: '10%',
      textAlign: 'center',
    }
    const detailsColumn: DataColumn = {
      headElement: <></>,
      bodyElement: (data) => (
        <TableRowDetailWithExpansion
          id={data?.grnaId || ''}
          isExpanded={checkForExpanded(data?.grnaId)}
          onClick={toggleDetail}
        />
      ),
      width: '10%',
      textAlign: 'center',
    }

    switch (product) {
      case PREDESIGNED_CRISPR_GRNA:
        return [
          chekBoxColumn,
          {
            headElement: (
              <FormattedMessage
                id="PREDESIGNED_CONTROL_MISSION_GRNA_ID"
                defaultMessage="MISSION™ gRNA ID"
              />
            ),
            bodyElement: (data) => data?.grnaId,
            textAlign: 'center',
            width: '18%',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_CATEGORY_crisprgrna"
                defaultMessage="gRNA Category"
              />
            ),
            bodyElement: (data) => {
              return data?.recommendedStatus === 'Recommended' ? (
                <div className={classes.topChoice}>
                  <DoneRoundedIcon className={classes.topChoiceIcon} />
                  <FormattedMessage
                    id="SEMI_CONF_TOP_CHOICE_crispgrna"
                    defaultMessage="Top Choice"
                  />
                </div>
              ) : (
                data?.recommendedStatus
              )
            },
            width: '14%',
            textAlign: 'center',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_CRISPR_GENE_SYMBOL"
                defaultMessage="Gene Symbol"
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.geneSymbol),
            width: '10%',
            textAlign: 'center',
          },
          geneIdColumn,
          {
            headElement: (
              <FormattedMessage
                id="CONF_EFFICIENCY_LABEL_crispgrna"
                defaultMessage="Efficiency Score"
              />
            ),
            headElementEndIcon: (
              <HoverToolTip
                toolTipText={
                  <div>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_EFFICIENCY_crisprgrna"
                      defaultMessage="Efficiency score from Doench, Fusi et al. (2016)"
                    />
                  </div>
                }
                fontSize="14"
                tooltipClass={classes.tooltipClass || undefined}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.efficiencyScore),
            width: '14%',
            textAlign: 'center',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_SPECIFICATION_LABEL_crispgrna"
                defaultMessage="Specificity Score"
              />
            ),
            headElementEndIcon: (
              <HoverToolTip
                toolTipText={
                  <div>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_SPECIFICITY_CRISPRGRNA_UPDATED"
                      defaultMessage="Specificity score from Hsu et al. (2013)"
                    />
                  </div>
                }
                fontSize="14"
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.specificityScore),
            width: '14%',
            textAlign: 'center',
          },
          speciesColumn,
          detailsColumn,
        ]
      case SANGERCRISPR:
      case ORF:
      case PREDESIGNED_SIRNA:
      case PREDESIGNED_SHRNA:
      default:
        return []
    }
  }

  const noResultsComponent = (
    <FormattedMessage id="NO_RESULTS_FOUND" defaultMessage="No results found" />
  )
  const tableExpandedComponent = (data: SemiConfigClone) => {
    return expandedList.includes(data?.[uniqueIdName] || '') ? (
      <TableExpanded data={data.moreInformation} product={product} />
    ) : (
      <></>
    )
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <Typography variant="h3">{tableTitle}</Typography>
      </Grid>
      <Grid item xs={12} className={classes.instructions}>
        <Typography variant="subtitle1">
          <FormattedMessage
            id="WEBINAR_PAGINATION"
            defaultMessage="Showing {pageStart} - {pageEnd} of {numberOfPages} Results"
            values={{
              pageStart: dataCount.pageStart,
              pageEnd: dataCount.pageEnd,
              numberOfPages: dataCount.totalCount,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.table}>
        <DataTable
          data={tableData}
          columns={getProductBasedTableColumns()}
          noDataComponent={noResultsComponent}
          captionText={tableTitle}
          afterRow={tableExpandedComponent}
        />
      </Grid>
    </Grid>
  )
}

export default ClonesTable
